/**
 * Teak Quote Storage
 *
 * localStorage-backed list of products a customer wants to request a
 * quote for. Survives reloads, works for guests, no account required.
 *
 * Item shape:
 *   {
 *     variantId:   "19655662982",            // numeric Shopify variant ID, as string
 *     productId:   "1234567890",             // numeric Shopify product ID, as string
 *     handle:      "hyde-park-garden-bench", // product handle
 *     title:       "Hyde Park Garden Bench",
 *     variantTitle:"120cm",                  // empty string if no variants
 *     priceCents:  77000,                    // integer cents
 *     currency:    "AUD",
 *     imageUrl:    "https://cdn.shopify.com/...",
 *     url:         "/products/hyde-park-garden-bench?variant=19655662982",
 *     quantity:    1,
 *     addedAt:     1716397200000
 *   }
 *
 * Events dispatched on `document`:
 *   teak:quote:changed   { items, count }   any modification
 *   teak:quote:added     { item }           single item added
 *   teak:quote:removed   { variantId }      single item removed
 *   teak:quote:cleared   {}                 list emptied
 */

(function () {
  if (window.TeakQuote) return;

  const STORAGE_KEY = 'teak_quote_v1';
  const MAX_QUANTITY = 999;

  function readRaw() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isValidItem);
    } catch {
      return [];
    }
  }

  function writeRaw(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Teak quote storage write failed', err);
    }
  }

  function isValidItem(item) {
    return (
      item &&
      typeof item === 'object' &&
      typeof item.variantId === 'string' &&
      item.variantId.length > 0 &&
      typeof item.title === 'string'
    );
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent('teak:quote:' + name, { detail }));
  }

  function findIndex(items, variantId) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].variantId === variantId) return i;
    }
    return -1;
  }

  function clampQuantity(qty) {
    const n = Math.floor(Number(qty));
    if (!Number.isFinite(n) || n < 1) return 1;
    if (n > MAX_QUANTITY) return MAX_QUANTITY;
    return n;
  }

  const TeakQuote = {
    STORAGE_KEY,

    all() {
      return readRaw();
    },

    count() {
      return readRaw().reduce((sum, item) => sum + item.quantity, 0);
    },

    uniqueCount() {
      return readRaw().length;
    },

    has(variantId) {
      return findIndex(readRaw(), String(variantId)) !== -1;
    },

    get(variantId) {
      const items = readRaw();
      const idx = findIndex(items, String(variantId));
      return idx === -1 ? null : items[idx];
    },

    add(item) {
      if (!isValidItem(item)) {
        console.warn('TeakQuote.add: invalid item', item);
        return null;
      }

      const items = readRaw();
      const idx = findIndex(items, item.variantId);
      const quantity = clampQuantity(item.quantity || 1);

      if (idx === -1) {
        const newItem = Object.assign({}, item, {
          quantity,
          addedAt: Date.now(),
        });
        items.push(newItem);
        writeRaw(items);
        dispatch('added', { item: newItem });
        dispatch('changed', { items, count: this.count() });
        return newItem;
      }

      items[idx].quantity = clampQuantity(items[idx].quantity + quantity);
      writeRaw(items);
      dispatch('added', { item: items[idx] });
      dispatch('changed', { items, count: this.count() });
      return items[idx];
    },

    setQuantity(variantId, quantity) {
      const items = readRaw();
      const idx = findIndex(items, String(variantId));
      if (idx === -1) return null;

      const qty = clampQuantity(quantity);
      items[idx].quantity = qty;
      writeRaw(items);
      dispatch('changed', { items, count: this.count() });
      return items[idx];
    },

    remove(variantId) {
      const items = readRaw();
      const idx = findIndex(items, String(variantId));
      if (idx === -1) return false;
      items.splice(idx, 1);
      writeRaw(items);
      dispatch('removed', { variantId: String(variantId) });
      dispatch('changed', { items, count: this.count() });
      return true;
    },

    clear() {
      writeRaw([]);
      dispatch('cleared', {});
      dispatch('changed', { items: [], count: 0 });
    },

    subscribe(handler) {
      const wrapped = (event) => handler(event.detail, event.type);
      document.addEventListener('teak:quote:changed', wrapped);
      return () => document.removeEventListener('teak:quote:changed', wrapped);
    },
  };

  window.TeakQuote = TeakQuote;

  // Cross-tab sync: when localStorage changes in another tab, fire the
  // changed event here so all UI updates without needing a reload.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    dispatch('changed', { items: readRaw(), count: TeakQuote.count() });
  });
})();
